import type { TemplateCategory, TemplateTag } from '@/lib/cms'

import { useEffect, useState } from 'react'
import Link from 'next/link'

import { Reveal } from '@/components/animation'
import { useTranslation } from '@/i18n'
import { Newsletter } from './newsletter'

interface FooterLinkItem {
    label: string
    href: string
}

function FooterLink({ label, href }: FooterLinkItem) {
    const external = href.startsWith('http')
    const inner = (
        <>
            <div>{label}</div>
            <div className='link-block-underline' />
        </>
    )

    if (external) {
        return (
            <a href={href} target='_blank' rel='noreferrer' className='link-block footer-link w-inline-block'>
                {inner}
            </a>
        )
    }

    return (
        <Link href={href} className='link-block footer-link w-inline-block'>
            {inner}
        </Link>
    )
}

const socials = [
    { label: 'LinkedIn', icon: 'https://wedoflow.cdn.modulify.website/images/wedoflow-in.svg', href: 'https://www.linkedin.com/company/wedoflow/', cls: 'linkedin' },
    { label: 'Instagram', icon: 'https://wedoflow.cdn.modulify.website/images/wedoflow-insta.svg', href: 'https://www.instagram.com/wedoflow/?hl=en', cls: '' },
    { label: 'Facebook', icon: 'https://wedoflow.cdn.modulify.website/images/wedoflow-fb.svg', href: 'https://www.facebook.com/wedoflow/', cls: '' },
    { label: 'Twitter', icon: 'https://wedoflow.cdn.modulify.website/images/wedoflow-twitter.svg', href: 'https://x.com/intent/follow?screen_name=wedoflow', cls: '' },
    { label: 'TikTok', icon: 'https://wedoflow.cdn.modulify.website/images/wedoflow-tiktok.svg', href: 'https://www.tiktok.com/@wedoflow', cls: '' },
    { label: 'YouTube', icon: 'https://wedoflow.cdn.modulify.website/images/wedoflow-youtube.svg', href: 'https://www.youtube.com/channel/UC2FWFwbfxsWbxUayV1-6Gzg', cls: '' }
]

interface FooterProps {
    topCategories?: TemplateCategory[]
    topTags?: TemplateTag[]
}

export function Footer({ topCategories, topTags }: FooterProps) {
    const { t, locale } = useTranslation()
    const [categories, setCategories] = useState<TemplateCategory[]>(topCategories ?? [])
    const [tags, setTags] = useState<TemplateTag[]>(topTags ?? [])

    useEffect(() => {
        if ((topCategories && topCategories.length) || (topTags && topTags.length)) return

        let active = true

        fetch(`/api/footer-meta?locale=${encodeURIComponent(locale)}`)
            .then((r) => r.json())
            .then((data) => {
                if (!active) return
                setCategories(data.categories ?? [])
                setTags(data.tags ?? [])
            })
            .catch(() => {})

        return () => {
            active = false
        }
    }, [topCategories, topTags, locale])

    const product: FooterLinkItem[] = [
        { label: t.footer.unlockAllAccess, href: 'https://app.wedoflow.com' },
        { label: t.footer.templates, href: '/' },
        { label: t.footer.ourFlow, href: '/our-flow' },
        { label: t.footer.insights, href: '/insights' },
        { label: t.footer.giveaway, href: '/get-free-premium-webflow-template' },
        { label: t.footer.cloneable, href: '/cloneable' },
        { label: t.footer.tags, href: '/tags' },
        { label: t.footer.affiliate, href: 'https://wonderlist.lemonsqueezy.com/affiliates' }
    ]

    const other: FooterLinkItem[] = [
        { label: t.footer.contact, href: '/contact' },
        { label: t.footer.privacy, href: '/privacy' },
        { label: t.footer.faq, href: '/faq' }
    ]

    return (
        <div className='footer'>
            <div className='container'>
                <div className='footer-flex-container'>
                    <Reveal>
                        <Link href='/' className='footer-logo-link'>
                            <img src='https://wedoflow.cdn.modulify.website/images/Nav-Logo-White.svg' alt='Wedoflow' className='footer-image' />
                        </Link>
                        <p className='grey-paragraph footer-flex-paragraph'>{t.footer.tagline}</p>
                    </Reveal>

                    <Reveal>
                        <h2 className='footer-heading'>{t.footer.product}</h2>
                        {product.map((link) => (
                            <FooterLink key={link.label} {...link} />
                        ))}
                        <div className='footer-secondary-section'>
                            <h2 className='footer-heading'>{t.footer.other}</h2>
                            {other.map((link) => (
                                <FooterLink key={link.label} {...link} />
                            ))}
                        </div>
                    </Reveal>

                    <Reveal>
                        <h2 className='footer-heading'>{t.footer.followUs}</h2>
                        {socials.map((social) => (
                            <a
                                key={social.label}
                                href={social.href}
                                target='_blank'
                                rel='noreferrer'
                                className='link-block social-media-footer-link w-inline-block'
                            >
                                <div className='social-text-holder'>
                                    <img src={social.icon} loading='lazy' alt={`${social.label} Wedoflow`} className={`social-media-footer-icon ${social.cls}`} />
                                    <div>{social.label}</div>
                                </div>
                                <div className='link-block-underline' />
                            </a>
                        ))}
                        <div className='footer-secondary-section'>
                            <h2 className='footer-heading'>{t.footer.topCategories}</h2>
                            {categories.map((category) => (
                                <Link key={category.slug} href={`/template-category/${category.slug}`} className='link-block footer-link w-inline-block'>
                                    <div>{category.name}</div>
                                    <div className='link-block-underline' />
                                </Link>
                            ))}
                        </div>
                    </Reveal>

                    <Reveal>
                        <h2 className='footer-heading'>{t.footer.topTags}</h2>
                        {tags.map((tag) => (
                            <Link key={tag.slug} href={`/tags/${tag.slug}`} className='link-block footer-link w-inline-block'>
                                <div>#{tag.name}</div>
                                <div className='link-block-underline' />
                            </Link>
                        ))}
                    </Reveal>
                </div>

                <div className='form-footer'>
                    <h4 className='no-margins'>{t.footer.mission}</h4>
                    <Newsletter />
                </div>

                <div className='footer-badge-row'>
                    <a href='https://verifieddr.com/website/wedoflow-com' target='_blank' rel='noreferrer'>
                        <img
                            src='https://verifieddr.com/badge/wedoflow-com.svg?metric=truedr'
                            alt='Verified DR - Verified Domain Rating for wedoflow.com'
                            width={220}
                            height={68}
                        />
                    </a>
                </div>
            </div>
        </div>
    )
}
